import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { fontScale } from '@/theme';
import { mq } from '../../../theme/scalingTokens';

export const useStepIndicatorStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalL),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('0.5rem'),
    flexWrap: 'wrap',
    rowGap: tokens.spacingVerticalXS,
    [mq.tablet]: {
      ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
      columnGap: tokens.spacingHorizontalXS,
    },
    [mq.mobile]: {
      ...shorthands.padding('0.375rem', '0.625rem'),
    },
  },
  tablistInner: {
    display: 'contents',
  },
  /**
   * Figma-aligned root: transparent over the page canvas, no inner card chrome.
   */
  rootFigma: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('999px'),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalL),
    boxShadow: tokens.shadow4,
  },
  stepText: {
    fontSize: fontScale.body,
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
  },
  separator: {
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    [mq.tablet]: {
      display: 'none',
    },
  },
  stepButton: {
    cursor: 'pointer',
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
    ...shorthands.borderRadius('0.25rem'),
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    ':focus-visible': {
      outlineColor: tokens.colorStrokeFocus2,
      outlineWidth: '2px',
      outlineStyle: 'solid',
    },
    [mq.tablet]: {
      display: 'none',
    },
  },
  stepButtonDisabled: {
    cursor: 'default',
    opacity: 0.5,
    ':hover': { backgroundColor: 'transparent' },
  },
  /**
   * Compact-mode override that keeps the step button (circle + separator)
   * visible at narrow viewports. Applied to all step buttons when the
   * `compactBreakpoint` prop opts in.
   */
  stepButtonCompactTablet: {
    [mq.tablet]: {
      display: 'flex',
    },
  },
  stepButtonCompactMobile: {
    [mq.mobile]: {
      display: 'flex',
    },
  },
  separatorCompactTablet: {
    [mq.tablet]: {
      display: 'flex',
    },
  },
  separatorCompactMobile: {
    [mq.mobile]: {
      display: 'flex',
    },
  },
  stepCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.5rem',
    height: '1.5rem',
    ...shorthands.borderRadius('50%'),
    fontSize: fontScale.caption,
    fontWeight: '600',
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    flexShrink: 0,
  },
  stepCircleActive: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  stepCircleCompleted: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  /**
   * Figma-aligned step circle (outlined, future/upcoming). Uses the dedicated
   * stepper accent tokens so the ring + number stay a clearly-visible blue in
   * dark mode (spec 101) without depending on the shared action surfaces.
   */
  stepCircleFigma: {
    backgroundColor: 'transparent',
    color: tokens.colorBrandForeground2,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  /** Figma-aligned active step: accent-blue fill, contrasting glyph. */
  stepCircleFigmaActive: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.borderColor(tokens.colorBrandBackground),
  },
  /** Figma-aligned completed step: accent-blue fill, contrasting check. */
  stepCircleFigmaCompleted: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.borderColor(tokens.colorBrandBackground),
  },
  stepLabel: {
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
  },
  stepLabelActive: {
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  /**
   * Compact-mode label: visually hidden but kept in the DOM so screen readers
   * still announce the step label. Applied to non-active steps when the
   * `compactBreakpoint` opts in.
   */
  stepLabelCompactTablet: {
    [mq.tablet]: {
      position: 'absolute',
      width: '1px',
      height: '1px',
      ...shorthands.padding(0),
      ...shorthands.margin(0),
      ...shorthands.overflow('hidden'),
      clip: 'rect(0 0 0 0)',
      whiteSpace: 'nowrap',
    },
  },
  stepLabelCompactMobile: {
    [mq.mobile]: {
      position: 'absolute',
      width: '1px',
      height: '1px',
      ...shorthands.padding(0),
      ...shorthands.margin(0),
      ...shorthands.overflow('hidden'),
      clip: 'rect(0 0 0 0)',
      whiteSpace: 'nowrap',
    },
  },
  stepLabelCompactAlways: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    ...shorthands.padding(0),
    ...shorthands.margin(0),
    ...shorthands.overflow('hidden'),
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
  stepButtonCompactAlways: {
    display: 'flex',
  },
  separatorCompactAlways: {
    display: 'flex',
  },
});
